import React, { useRef, useEffect } from "react";
import { Input, InputGroup, InputRightElement } from "@chakra-ui/react";
import "flatpickr/dist/flatpickr.min.css";
import flatpickr from "flatpickr";
import { CalendarIcon, CloseIcon } from "components/Icons";
import "./DatePicker.css";
import dayjs from "dayjs";
interface Props {
  minDate?: Date;
  maxDate?: Date;
  onChange: (date?: Date) => void;
  selected?: Date;
  placeholder?: string;
  excludeDates?: Date[];
  filterDate?: (date: Date) => boolean;
  disabled?: boolean;
  isDisableClear?: boolean;
  className?: string;
  dateFormat?: string;
  isDisabledPast?: boolean;
  isDisabledFuture?: boolean;
  name?: string;
  "data-testid"?: string;
}

const DatePicker = ({
  onChange,
  selected,
  minDate,
  maxDate,
  placeholder = "Click to select a date",
  disabled = false,
  className,
  dateFormat,
  isDisabledPast,
  isDisabledFuture,
  excludeDates,
  filterDate,
  isDisableClear,
  name,
  "data-testid": dataTestId
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (inputRef.current) {
      flatpickr(inputRef.current, {
        dateFormat: dateFormat ?? "Y-m-d",
        minDate: isDisabledPast ? "today" : minDate,
        maxDate: isDisabledFuture ? "today" : maxDate,
        disable: [
          ...(excludeDates || []),
          (date) => (filterDate ? !filterDate(date) : false),
        ],
        onChange: (selectedDates) => {
          // format date to yyyy-MM-ddTHH:mm:ssZ
          const date = selectedDates[0];
          if (date) {
            const formattedDate = dayjs(date).format("YYYY-MM-DDTHH:mm:ssZ");
            onChange(new Date(formattedDate));
          } else {
            onChange(undefined);
          }
        },
      });
    }
  }, [
    inputRef,
    maxDate,
    minDate,
    dateFormat,
    onChange,
    excludeDates,
    filterDate,
    isDisabledPast,
    isDisabledFuture,
  ]);

  const handleOpenCalendar = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  return (
    <InputGroup isolation="isolate">
      <Input
        className={className}
        value={
          selected
            ? new Date(
              selected.getTime() - selected.getTimezoneOffset() * 60000
            )
              .toISOString()
              .split("T")[0]
            : ""
        }
        disabled={disabled}
        ref={inputRef}
        placeholder={placeholder}
        data-testid={dataTestId}
      />
      <InputRightElement>
        {selected && !isDisableClear ? (
          <CloseIcon
            data-testid={`clear-date-${name}`}
            color="#1175B5"
            cursor="pointer"
            pathFill="#1175B5"
            onClick={(e) => {
              onChange(undefined);
              if (inputRef.current) {
                inputRef.current.value = "";
              }
            }}
          />
        ) : (
          <CalendarIcon onClick={handleOpenCalendar} />
        )}
      </InputRightElement>
    </InputGroup>
  );
};

export default DatePicker;
